
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Link2, LineChart, Mail, Repeat, ShieldCheck } from 'lucide-react';

export const IntegrationsDoc = () => (
  <div className="space-y-12">
    <section>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
            GhostPay integrates with leading protocols and services to provide accurate data, liquidity, and communication channels.
        </p>
    </section>

    <div className="space-y-8">
        <section>
            <h3 className="text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-2">
                <LineChart size={20} className="text-nobel-gold"/> Oracles
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">Pyth Network</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">Solana / Sui</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">Used for sub-second, high-fidelity price feeds for SOL, SUI, and other major assets to ensure accurate conversion rates at checkout.</p>
                </div>
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">Chainlink</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">EVM / Solana</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">Provides robust, decentralized data feeds for verification and cross-chain asset pricing.</p>
                </div>
            </div>
        </section>

        <section>
            <h3 className="text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-2">
                <Repeat size={20} className="text-nobel-gold"/> DEX Aggregators
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">Jupiter</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">Solana</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">Powering our swap functionality on Solana, offering the best rates by aggregating liquidity across all major DEXs.</p>
                </div>
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">Cetus</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">Sui</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">The primary liquidity protocol used for swaps and settlements on the Sui network.</p>
                </div>
            </div>
        </section>

        <section>
            <h3 className="text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-2">
                <ShieldCheck size={20} className="text-nobel-gold"/> Cross-Chain & Privacy
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">LayerZero</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">OFT Standard</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">Utilizing the Omnichain Fungible Token (OFT) standard for native burn-and-mint bridging of GHOST assets between Sui and Solana.</p>
                </div>
                <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-stone-900 dark:text-white">Light Protocol</h4>
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">ZK Compression</span>
                    </div>
                    <p className="text-sm text-stone-600 dark:text-stone-400">Enables rent-free state compression on Solana, allowing millions of escrow accounts to exist at a fraction of the cost.</p>
                </div>
            </div>
        </section>

        <section>
            <h3 className="text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-2">
                <Mail size={20} className="text-nobel-gold"/> Infrastructure
            </h3>
            <div className="p-5 bg-white dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-stone-900 dark:text-white">Mailgun</h4>
                    <span className="text-[10px] uppercase font-bold tracking-widest bg-stone-100 dark:bg-white/10 px-2 py-1 rounded">Email API</span>
                </div>
                <p className="text-sm text-stone-600 dark:text-stone-400">Handles transactional emails for invoices, payment receipts, and system alerts, ensuring reliable delivery.</p>
            </div>
        </section>
    </div>
  </div>
);
